import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  EParcelStoreKind,
  IMailroomSettings,
  IParcelLookup,
  IParcelStore,
  IRegistryOffice,
} from "dto/Mailroom/IMailroom";

// Serwis biura podawczego: słowniki (rodzaje załączników, formy doręczenia),
// biura podawcze, składy oraz ustawienia urzędu (domyślny dekretujący).
// Kreator bierze słowniki z /incomingParcel/init; te hooki zasilają panel admina.

export const mailroomQueryKeys = {
  attachmentTypes: "mailroom.attachmentTypes",
  deliveryKinds: "mailroom.deliveryKinds",
  registryOffices: "mailroom.registryOffices",
  parcelStores: "mailroom.parcelStores",
  settings: "mailroom.settings",
};

export interface ISaveLookupRequest {
  Id?: number | null;
  Name: string;
  SortOrder: number;
}

export interface ISaveRegistryOfficeRequest {
  Id?: string | null;
  Name: string;
  Symbol?: string | null;
  IsDefault: boolean;
}

export interface ISaveParcelStoreRequest {
  Id?: string | null;
  Name: string;
  NumberFormat: string;
  Kind: EParcelStoreKind;
  IsElectronic: boolean;
  ContinuousForYear: boolean;
  ContinuousForUser: boolean;
  IsDefault: boolean;
  IsActive: boolean;
}

export interface IUpdateMailroomSettingsRequest {
  DefaultReceiverUserId?: string | null;
  DefaultRegistryOfficeId?: string | null;
  DefaultParcelStoreId?: string | null;
}

// ── Słowniki ──────────────────────────────────────────────────────────────

const getAttachmentTypes = (): Promise<{ data: IParcelLookup[] }> =>
  axiosInstance.get("mailroom/attachmentTypes");

const useGetAttachmentTypes = () =>
  useCustomQuery(mailroomQueryKeys.attachmentTypes, () => getAttachmentTypes());

const getDeliveryKinds = (): Promise<{ data: IParcelLookup[] }> =>
  axiosInstance.get("mailroom/deliveryKinds");

const useGetDeliveryKinds = () =>
  useCustomQuery(mailroomQueryKeys.deliveryKinds, () => getDeliveryKinds());

const saveAttachmentType = (
  payload: ISaveLookupRequest,
): Promise<{ data: IParcelLookup }> =>
  axiosInstance.post("mailroom/attachmentTypes", payload);

const useSaveAttachmentType = () => useCustomMutation(saveAttachmentType);

const saveDeliveryKind = (
  payload: ISaveLookupRequest,
): Promise<{ data: IParcelLookup }> =>
  axiosInstance.post("mailroom/deliveryKinds", payload);

const useSaveDeliveryKind = () => useCustomMutation(saveDeliveryKind);

const removeAttachmentType = (id: number): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/attachmentTypes/${id}`);

const useRemoveAttachmentType = () => useCustomMutation(removeAttachmentType);

const removeDeliveryKind = (id: number): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/deliveryKinds/${id}`);

const useRemoveDeliveryKind = () => useCustomMutation(removeDeliveryKind);

// ── Biura podawcze / składy ────────────────────────────────────────────────

const getRegistryOffices = (): Promise<{ data: IRegistryOffice[] }> =>
  axiosInstance.get("mailroom/registryOffices");

const useGetRegistryOffices = () =>
  useCustomQuery(mailroomQueryKeys.registryOffices, () => getRegistryOffices());

const saveRegistryOffice = (
  payload: ISaveRegistryOfficeRequest,
): Promise<{ data: IRegistryOffice }> =>
  axiosInstance.post("mailroom/registryOffices", payload);

const useSaveRegistryOffice = () => useCustomMutation(saveRegistryOffice);

const removeRegistryOffice = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/registryOffices/${id}`);

const useRemoveRegistryOffice = () => useCustomMutation(removeRegistryOffice);

const getParcelStores = (): Promise<{ data: IParcelStore[] }> =>
  axiosInstance.get("mailroom/parcelStores");

const useGetParcelStores = () =>
  useCustomQuery(mailroomQueryKeys.parcelStores, () => getParcelStores());

const saveParcelStore = (
  payload: ISaveParcelStoreRequest,
): Promise<{ data: IParcelStore }> =>
  axiosInstance.post("mailroom/parcelStores", payload);

const useSaveParcelStore = () => useCustomMutation(saveParcelStore);

const removeParcelStore = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/parcelStores/${id}`);

const useRemoveParcelStore = () => useCustomMutation(removeParcelStore);

// ── Ustawienia urzędu ──────────────────────────────────────────────────────

const getSettings = (): Promise<{ data: IMailroomSettings }> =>
  axiosInstance.get("mailroom/settings");

const useGetSettings = () =>
  useCustomQuery(mailroomQueryKeys.settings, () => getSettings());

const updateSettings = (
  payload: IUpdateMailroomSettingsRequest,
): Promise<{ data: IMailroomSettings }> =>
  axiosInstance.put("mailroom/settings", payload);

const useUpdateSettings = () => useCustomMutation(updateSettings);

export default {
  useGetAttachmentTypes,
  useGetDeliveryKinds,
  useSaveAttachmentType,
  useSaveDeliveryKind,
  useRemoveAttachmentType,
  useRemoveDeliveryKind,
  useGetRegistryOffices,
  useSaveRegistryOffice,
  useRemoveRegistryOffice,
  useGetParcelStores,
  useSaveParcelStore,
  useRemoveParcelStore,
  useGetSettings,
  useUpdateSettings,
};
